import { useEffect, useState } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  Container,
  Typography,
  Grid,
  Box,
  Divider,
  Stack,
} from "@mui/material";
import { useReportStore } from "../../data/ReportStore";
import {
  useLeagueApi,
  useLeagueEnvironment,
} from "../../data/LeagueEnvironmentProvider";
import { ExplorerEnvironment } from "../../data/Cache";
import PastGame from "../Elements/GameDisplay/PastGame";
import UpcomingGame from "../Elements/GameDisplay/UpcomingGame";
import GamesSkeleton from "../Elements/GameDisplay/GamesSkeleton";
import { Game, GameWithID } from "../../models/Game";
import TeamTileList from "../Elements/TeamTileList/TeamTileList";
import GameDisplayStack from "../Elements/GameDisplay/GameDisplayStack";

// Helper function to check if we need a season divider
const needsSeasonDivider = (
  currentGame: Game | GameWithID,
  previousGame: Game | GameWithID | null,
) => {
  if (!previousGame) return false;
  return (
    previousGame.seasonType === "Preseason" &&
    currentGame.seasonType === "Regular Season"
  );
};

// Helper function to get appropriate divider text based on context
const getSeasonDividerText = (
  currentGame: Game | GameWithID,
  previousGame: Game | GameWithID | null,
  isPastGames: boolean,
) => {
  if (!needsSeasonDivider(currentGame, previousGame)) return "";

  if (isPastGames) {
    // For past games, we're going from newer to older, so "Regular Season Ends"
    return "Regular Season Ends";
  } else {
    // For upcoming games, we're going from older to newer, so "Regular Season Begins"
    return "Regular Season Begins";
  }
};

const GameLogFetchLimit = 82;
const GameLogDisplayLimit = 5;
const GameDisplayGap = 2;

export default function Home() {
  const { setTeam, setReportType } = useReportStore((store) => store);
  const [upcomingGames, setUpcomingGames] = useState<Game[] | null>(null);
  const [prevGames, setPrevGames] = useState<GameWithID[] | null>(null);
  const api = useLeagueApi();
  const environment = useLeagueEnvironment();

  usePageTitle(
    environment === ExplorerEnvironment.NBA
      ? "NBA Team Reports - Explorer"
      : "G League Team Reports - Explorer",
  );

  useEffect(() => {
    setTeam(null);
    setReportType(null);
    setUpcomingGames(null);
    setPrevGames(null);

    api
      .getUpcomingGames()
      .then(({ data }) => {
        setUpcomingGames(data);
      })
      .catch((err) => {
        console.log("error getting upcoming games: ", err);
      });
    api.getGameLog(GameLogFetchLimit).then(({ data }) => {
      setPrevGames(data);
    });
  }, [setTeam, setReportType, api]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        pt: 2,
        ".MuiTypography-root": {
          whiteSpace: "nowrap",
        },
      }}
    >
      <Stack gap={4}>
        <Grid container spacing={4}>
          <Grid item xs={12} lg={6}>
            <Typography variant="h3" gutterBottom>
              Recent Games
            </Typography>
            {prevGames === null ? (
              <Stack gap={GameDisplayGap}>
                <GamesSkeleton num={GameLogDisplayLimit} />
              </Stack>
            ) : prevGames.length === 0 ? (
              <Box
                pb={4}
                pt={3.5}
                px={6}
                mb={2}
                sx={{
                  border: "2px solid black",
                  borderRadius: "15px",
                  backgroundColor: "white",
                }}
              >
                <Typography variant="h3">No recent game reports</Typography>
              </Box>
            ) : (
              <GameDisplayStack
                gap={GameDisplayGap}
                displayCount={GameLogDisplayLimit}
              >
                {prevGames.map((game, index) => {
                  const previousGame = index > 0 ? prevGames[index - 1] : null;
                  return (
                    <Box key={`${game.date}:${game.opponentInfo}}`}>
                      {needsSeasonDivider(game, previousGame) && (
                        <Box my={3}>
                          <Divider sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {getSeasonDividerText(game, previousGame, true)}
                            </Typography>
                          </Divider>
                        </Box>
                      )}
                      <PastGame game={game} />
                    </Box>
                  );
                })}
              </GameDisplayStack>
            )}
          </Grid>

          <Grid item xs={12} lg={6}>
            <Typography variant="h3" gutterBottom>
              Upcoming Games
            </Typography>
            {upcomingGames === null ? (
              <Stack gap={GameDisplayGap}>
                <GamesSkeleton num={GameLogDisplayLimit} />
              </Stack>
            ) : upcomingGames.length === 0 ? (
              <Box
                pb={4}
                pt={3.5}
                px={6}
                mb={2}
                sx={{
                  border: "2px solid black",
                  borderRadius: "15px",
                  backgroundColor: "white",
                }}
              >
                <Typography variant="h3">No upcoming games</Typography>
              </Box>
            ) : (
              <GameDisplayStack
                gap={GameDisplayGap}
                displayCount={GameLogDisplayLimit}
              >
                {upcomingGames.map((game, index) => {
                  const previousGame =
                    index > 0 ? upcomingGames[index - 1] : null;
                  return (
                    <Box key={`${game.date}:${game.opponentInfo}}`}>
                      {needsSeasonDivider(game, previousGame) && (
                        <Box my={3}>
                          <Divider sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {getSeasonDividerText(game, previousGame, false)}
                            </Typography>
                          </Divider>
                        </Box>
                      )}
                      <UpcomingGame game={game} />
                    </Box>
                  );
                })}
              </GameDisplayStack>
            )}
          </Grid>
        </Grid>
        <TeamTileList />
      </Stack>
    </Container>
  );
}
